import * as React from 'react';
import { translate } from 'react-i18next';
import Dropzone, { DropzoneProps } from 'react-dropzone';

import * as c from 'models/constants';
import { Button } from 'components';
import { formatInteger } from 'helpers/common';
require('./style.css');

interface IProps {
	isButton?: boolean;
	buttonText?: string;
	acceptFileTypes: string;
	isMultiple: boolean;
	name?: string;
	/* show only plain image input without loader and additional styles: */
	plainInput?: boolean;

	// pass ref to use in parent
	dropZoneRefName?: any;

	/* functions props */
	fileUploadedCallback?: (data?: any) => void;
	fileUploadFailed?: (error: any) => void;
	onUploadButtonClick?: (event: any) => void;
	t?: (key: string, options?: any) => string;
	showLoader?: () => void;
	hideLoader?: () => void;
	usage?: 'admin' | 'portal';
}

interface IPropsExtended extends IProps {
	dispatch: any;
	apiUrl: string;
	shopId?: string;
}

interface IPropsCustom extends IProps {
	customFileHandler: undefined | null | ((files: File[]) => Promise<void>);
}

interface IState {
	currentImageId?: string;
	currentImageUrl?: string;
	loading?: boolean;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class FileUpload extends React.Component<IPropsExtended | IPropsCustom, IState> {
	private isCancelled: boolean = false;

	public componentWillUnmount() {
		this.isCancelled = true;
	}

	public handleCustomUpload(files: File[]) {
		if (!('customFileHandler' in this.props)) {
			return;
		}
		const { customFileHandler } = this.props;
		if (!this.isCancelled) {
			this.setState({ loading: true });
			this.props.showLoader();
		}
		customFileHandler(files)
			.then(() => {
				if (this.props.fileUploadedCallback) {
					this.props.fileUploadedCallback();
				}
				if (!this.isCancelled) {
					this.setState({ loading: false });
					this.props.hideLoader();
				}
			})
			.catch((e) => {
				if (!this.isCancelled) {
					this.setState({ loading: false });
					this.props.hideLoader();
				}
				if (this.props.fileUploadFailed) {
					this.props.fileUploadFailed(e);
				}
			});
	}

	public uploadFile(data: File[]) {
		if ('customFileHandler' in this.props) {
			return;
		}

		const { shopId } = this.props;

		return (_, getState) => {
			if ('customFileHandler' in this.props) {
				return;
			}
			const formData = new FormData();
			formData.append('file', data[0]);
			if (shopId) {
				formData.append('shopId', shopId);
			}

			if (!this.isCancelled) {
				this.setState({ loading: true });
				this.props.showLoader();
			}

			const { apiUrl } = this.props;

			return fetch(getState().fetchDefault.baseAPI + apiUrl, {
				method: 'POST',
				body: formData,
				credentials: 'include',
				headers: {
					Authorization: getState().fetchDefault.requestInit.headers.Authorization,
				},
			})
				.then((res) => {
					if (res.ok) {
						const contentType = res.headers.get('content-type');
						if (contentType && contentType.indexOf('application/json') !== -1) {
							return res.json().then((data) => {
								if (this.props.fileUploadedCallback) {
									this.props.fileUploadedCallback(data);
								}
								if (!this.isCancelled) {
									this.setState({ loading: false });
									this.props.hideLoader();
								}
							});
						} else {
							if (this.props.fileUploadedCallback) {
								this.props.fileUploadedCallback();
							}
							if (!this.isCancelled) {
								this.setState({ loading: false });
								this.props.hideLoader();
							}
						}
					} else {
						if (!this.isCancelled) {
							this.setState({ loading: false });
							this.props.hideLoader();
						}
						const status: number = res.status;
						const statusText: string = res.statusText;
						return res
							.json()
							.then((res) => {
								res.status = status;
								res.statusText = statusText;
								if (this.props.fileUploadFailed) {
									this.props.fileUploadFailed(res);
								}
							})
							.catch(() => {
								const message: any = { status, statusText };
								if (!this.isCancelled) {
									this.setState({ loading: false });
									this.props.hideLoader();
								}
								if (this.props.fileUploadFailed) {
									this.props.fileUploadFailed(message);
								}
							});
					}
				})
				.catch((err) => {
					if (!this.isCancelled) {
						this.setState({ loading: false });
						this.props.hideLoader();
					}
					if (this.props.fileUploadFailed) {
						this.props.fileUploadFailed(err);
					}
				});
		};
	}

	public render() {
		const { t, acceptFileTypes, isMultiple, isButton, buttonText, name } = this.props;

		// show dropzone control
		const dropZoneProps: DropzoneProps = {
			accept: acceptFileTypes,
			multiple: isMultiple,
			onDrop: (acceptedFiles, fileRejections) => {
				if (acceptedFiles.length > 0) {
					if ('customFileHandler' in this.props) {
						this.handleCustomUpload(acceptedFiles);
					} else {
						this.props.dispatch(this.uploadFile(acceptedFiles));
					}
				} else if (fileRejections.length > 0 && this.props.fileUploadFailed) {
					const acceptFileTypesArray = acceptFileTypes.split(' ');
					if (fileRejections[0].file.size > c.MAX_IMAGE_SIZE) {
						this.props.fileUploadFailed({
							status: 422,
							statusText: t('FileUpload_Error_FileSizeTooLarge'),
							error: [t('FileUpload_Error_FileSizeTooLarge', { count: formatInteger(c.MAX_IMAGE_SIZE / 1000) })],
						});
					} else if (acceptFileTypesArray.indexOf(fileRejections[0].file.type) === -1) {
						this.props.fileUploadFailed({
							status: 422,
							statusText: t('FileUpload_Error_FileTypeNotSupported'),
							error: [t('FileUpload_Error_FileTypeNotSupported')],
						});
					}
				}
			},
		};

		let content: JSX.Element = null;

		if (isButton) {
			content = (
				<Dropzone {...dropZoneProps}>
					{({ getRootProps, getInputProps }) => (
						<div {...getRootProps({ className: 'dropZoneButton' })}>
							<input {...getInputProps({ name })} />
							<Button type="button" usage={this.props.usage}>
								{buttonText}
							</Button>
						</div>
					)}
				</Dropzone>
			);
		}
		return content;
	}
}

export { FileUpload };
